import { BookOutlined, CheckCircleOutlined, CloseCircleOutlined, ReadOutlined } from '@ant-design/icons';
import { Empty, Flex, List, theme, Tooltip, Typography } from 'antd';
import type { GestoDeEstudo } from '@titularis/api-client';
import { formatarData } from '../../dominio';
import { haQuantoTempo } from './vocabulario';

/**
 * O que a conta abriu por último, nos três materiais misturados.
 *
 * **É a resposta literal a "não tenho visibilidade do que estão fazendo".** Os
 * números agregados dizem quanto; esta lista diz **o quê** - a questão 14 do
 * Enac 2025.1, o art. 216-A da Lei 6.015/1973 -, e é lendo isso que se descobre
 * que três pessoas diferentes pararam na mesma questão.
 *
 * Misturados, e não em três abas, porque a leitura é cronológica: a sequência
 * "respondeu, errou, abriu a resolução, foi ler o artigo" só aparece quando os
 * três estão na mesma linha do tempo. Separados, viram três listas de onde
 * ninguém tira conclusão nenhuma.
 *
 * O corte vem do servidor - as quinze mais recentes de cada tipo -, e é por tipo
 * de propósito: uma maratona de legislação empurraria a última resposta para
 * fora de uma lista cortada no total.
 */
const ICONE = {
  resposta: <BookOutlined />,
  resolucao: <ReadOutlined />,
  artigo: <ReadOutlined />,
} as const;

const NOME = {
  resposta: 'Respondeu',
  resolucao: 'Abriu a resolução de',
  artigo: 'Leu o comentário de',
} as const;

export function UltimosGestos({ gestos }: { gestos: GestoDeEstudo[] }) {
  const { token } = theme.useToken();

  if (gestos.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Esta conta ainda não abriu nada."
      />
    );
  }

  return (
    <List
      size="small"
      dataSource={gestos}
      /*
       * Rola dentro do cartão em vez de esticar a página: são até quarenta e
       * cinco linhas, e a ficha tem seis blocos depois deste.
       */
      style={{ maxHeight: 360, overflowY: 'auto' }}
      renderItem={(gesto) => (
        <List.Item>
          <Flex align="center" gap={token.marginXS} style={{ width: '100%' }}>
            <Typography.Text type="secondary">{ICONE[gesto.tipo]}</Typography.Text>

            <Flex vertical gap={0} flex={1} style={{ minWidth: 0 }}>
              {/* O balão devolve o que o corte levou: a prova, quase sempre. */}
              <Typography.Text ellipsis={{ tooltip: `${NOME[gesto.tipo]} ${gesto.alvo}` }}>
                <Typography.Text type="secondary">{NOME[gesto.tipo]} </Typography.Text>
                {gesto.alvo}
              </Typography.Text>
            </Flex>

            {/*
              Desfeita não acertou nem errou - a resposta saiu da conta do aluno -,
              e por isso não ganha ícone nenhum em vez de ganhar o vermelho.
            */}
            {gesto.acertou === null ? null : gesto.acertou ? (
              <Tooltip title="Acertou">
                <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              </Tooltip>
            ) : (
              <Tooltip title="Errou">
                <CloseCircleOutlined style={{ color: token.colorError }} />
              </Tooltip>
            )}

            <Tooltip title={formatarData(gesto.em)}>
              <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                {haQuantoTempo(gesto.em)}
              </Typography.Text>
            </Tooltip>
          </Flex>
        </List.Item>
      )}
    />
  );
}
